<template>
  <div class="legal-page">
    <!-- Header -->
    <header class="main-header" :class="{ 'scrolled': isScrolled }">
      <div class="header-inner">
        <!-- Logo -->
        <router-link to="/" class="brand-logo-link">
          <div class="logo-mark">
            <svg viewBox="0 0 100 100" class="diamond-svg">
              <polygon points="50,12 85,38 50,88 15,38" fill="none" stroke="#9E6D2B" stroke-width="5" stroke-linejoin="round" stroke-linecap="round" />
              <line x1="15" y1="38" x2="85" y2="38" stroke="#9E6D2B" stroke-width="4" stroke-linecap="round" />
              <polyline points="32,12 28,38 50,88 72,38 68,12" fill="none" stroke="#9E6D2B" stroke-width="4" stroke-linejoin="round" stroke-linecap="round" />
              <polyline points="32,12 50,38 68,12" fill="none" stroke="#9E6D2B" stroke-width="4" stroke-linejoin="round" stroke-linecap="round" />
            </svg>
          </div>
          <div class="logo-text-block">
            <div class="brand-name">JEWEL<span class="brand-highlight">BIZ</span></div>
            <div class="brand-subtext">SMART JEWELLERY BUSINESS MANAGEMENT</div>
          </div>
        </router-link>

        <!-- Desktop Nav -->
        <nav class="desktop-nav d-none d-lg-flex">
          <router-link to="/" class="nav-link">
            <v-icon x-small class="mr-1">mdi-arrow-left</v-icon>
            Back to Home
          </router-link>
          <router-link to="/privacy-policy" class="nav-link">Privacy Policy</router-link>
          <router-link to="/book-demo" class="nav-link">Book a Demo</router-link>
        </nav>

        <!-- Right Header Actions -->
        <div class="header-actions">
          <router-link to="/" class="btn-back-nav d-inline-flex d-lg-none mr-2">
            <v-icon small class="mr-1">mdi-arrow-left</v-icon>
            Home
          </router-link>
          <router-link to="/login" class="btn-signin-gold">
            Sign In
            <v-icon small class="ml-1">mdi-arrow-right</v-icon>
          </router-link>
        </div>
      </div>
    </header>

    <!-- Hero Banner -->
    <section class="legal-hero">
      <div class="container-luxury">
        <div class="legal-kicker">
          <span class="kicker-sparkle">✦</span>
          <span>LEGAL & COMPLIANCE</span>
          <span class="kicker-sparkle">✦</span>
        </div>
        <h1 class="legal-title">Terms & Conditions</h1>
        <p class="legal-subtitle">
          Terms of service and subscription agreement governing the use of JewelBiz Jewellery ERP software.
        </p>
        <div class="meta-badge-row">
          <span class="meta-pill"><v-icon x-small color="#9E6D2B" class="mr-1">mdi-file-document-outline</v-icon> SaaS Commercial Agreement</span>
          <span class="meta-pill"><v-icon x-small color="#9E6D2B" class="mr-1">mdi-calendar-check</v-icon> Effective: September 2026</span>
          <span class="meta-pill"><v-icon x-small color="#9E6D2B" class="mr-1">mdi-scale-balance</v-icon> Indian IT & Commercial Laws</span>
        </div>
      </div>
    </section>

    <!-- Main Content Grid -->
    <section class="legal-content-section">
      <div class="container-luxury">
        <div class="legal-layout-grid">
          <!-- Sidebar Navigation -->
          <aside class="legal-sidebar d-none d-md-block">
            <div class="sidebar-sticky-card">
              <h4 class="sidebar-heading">Document Contents</h4>
              <ul class="toc-list">
                <li><a href="#section-1" :class="{ 'active': activeSection === 'section-1' }">1. Acceptance & Eligibility</a></li>
                <li><a href="#section-2" :class="{ 'active': activeSection === 'section-2' }">2. Software License & Scope</a></li>
                <li><a href="#section-3" :class="{ 'active': activeSection === 'section-3' }">3. Showroom Accounts & Security</a></li>
                <li><a href="#section-4" :class="{ 'active': activeSection === 'section-4' }">4. Bullion Rates & Tax Invoicing</a></li>
                <li><a href="#section-5" :class="{ 'active': activeSection === 'section-5' }">5. Subscription & Payment</a></li>
                <li><a href="#section-6" :class="{ 'active': activeSection === 'section-6' }">6. Service Uptime & SLA</a></li>
                <li><a href="#section-7" :class="{ 'active': activeSection === 'section-7' }">7. Intellectual Property</a></li>
                <li><a href="#section-8" :class="{ 'active': activeSection === 'section-8' }">8. Liability & Jurisdiction</a></li>
              </ul>

              <div class="sidebar-action-box mt-6">
                <router-link to="/privacy-policy" class="sidebar-link-btn">
                  View Privacy Policy →
                </router-link>
              </div>
            </div>
          </aside>

          <!-- Main Document Body -->
          <div class="legal-body-card">
            <!-- Section 1 -->
            <div id="section-1" class="legal-section">
              <div class="section-number-tag">01</div>
              <h2 class="section-title">Acceptance & Eligibility</h2>
              <p>
                These Terms and Conditions ("Terms", "Agreement") constitute a legally binding agreement between your jewellery enterprise ("Subscriber", "Client", "You") and <strong>JewelBiz</strong> ("Company", "We", "Us").
              </p>
              <p>
                By creating an account, accessing the platform, or subscribing to our cloud services, you represent that you have legal authority to bind your retail jewellery business, wholesale partnership, or manufacturing unit to these Terms.
              </p>
            </div>

            <!-- Section 2 -->
            <div id="section-2" class="legal-section">
              <div class="section-number-tag">02</div>
              <h2 class="section-title">Software License & Scope of Service</h2>
              <p>
                JewelBiz grants you a non-exclusive, non-transferable, revocable SaaS subscription license to access our platform for standard jewellery operations, including:
              </p>
              <ul class="styled-legal-list">
                <li><strong>Point-of-Sale (POS) & GST Billing:</strong> Generating tax invoices, estimate bills, and return vouchers adhering to Indian GST and HUID standards.</li>
                <li><strong>Inventory & Hallmarking Management:</strong> Tracking gold, diamond, silver, and platinum stock with gross weight, net weight, stone details, and barcode tracking.</li>
                <li><strong>Karigar Workshop Ledger:</strong> Tracking raw metal disbursements, scrap recovery, melting adjustments, and wastage (V.A.) records.</li>
                <li><strong>Gold Scheme & Passbook Management:</strong> Enrolling customers in monthly purchase plans, tracking digital payments, and calculating scheme bonuses upon maturity.</li>
              </ul>
            </div>

            <!-- Section 3 -->
            <div id="section-3" class="legal-section">
              <div class="section-number-tag">03</div>
              <h2 class="section-title">Showroom Accounts & Staff Credentials</h2>
              <p>
                You are solely responsible for maintaining the confidentiality of your administrative and staff credentials:
              </p>
              <ul class="styled-legal-list">
                <li><strong>Role Permissions:</strong> You must configure appropriate role-based permissions (e.g., restricting sales staff from viewing supplier wholesale rates or modifying audit records).</li>
                <li><strong>Account Integrity:</strong> Immediate notification to JewelBiz is required if you suspect any unauthorized access or compromise of admin credentials.</li>
                <li><strong>Staff Offboarding:</strong> You are responsible for deactivating staff accounts promptly upon employment termination.</li>
              </ul>
            </div>

            <!-- Section 4 -->
            <div id="section-4" class="legal-section">
              <div class="section-number-tag">04</div>
              <h2 class="section-title">Bullion Rates & Tax Invoicing Disclaimer</h2>
              <div class="callout-gold-box">
                <v-icon color="#9E6D2B" class="mr-2">mdi-alert-circle-outline</v-icon>
                <strong>Daily Board Rate Responsibility:</strong> Store administrators must confirm their daily showroom gold and silver board rates prior to billing.
              </div>
              <p>
                While JewelBiz provides automated live market price feeds from accredited bullion rate providers (GoldAPI), the software operator retains ultimate responsibility for setting final daily showroom rates, making charges, custom discounts, and ensuring statutory tax percentages conform to local state GST guidelines.
              </p>
            </div>

            <!-- Section 5 -->
            <div id="section-5" class="legal-section">
              <div class="section-number-tag">05</div>
              <h2 class="section-title">Subscription, Billing & Renewals</h2>
              <p>
                JewelBiz operates on transparent SaaS subscription plans (monthly or annual billing tiers):
              </p>
              <ul class="styled-legal-list">
                <li><strong>Payment Schedules:</strong> Subscription invoices must be settled within 10 days of billing date.</li>
                <li><strong>Feature Add-ons:</strong> Specialized add-ons (such as custom multi-branch clustering or dedicated WhatsApp API credits) are billed per active usage.</li>
                <li><strong>Refund Policy:</strong> Setup fees and consumed monthly subscription periods are non-refundable. Custom development work is billed according to mutually signed Statements of Work (SOW).</li>
              </ul>
            </div>

            <!-- Section 6 -->
            <div id="section-6" class="legal-section">
              <div class="section-number-tag">06</div>
              <h2 class="section-title">System Availability & SLA</h2>
              <p>
                We maintain a <strong>99.9% uptime target</strong> for all production server clusters. Scheduled maintenance is conducted during non-business hours (typically between 01:00 AM and 04:00 AM IST) with prior dashboard notice.
              </p>
              <p>
                Daily automated encrypted backups are stored across redundant AWS cloud regions to ensure disaster recovery and zero data loss.
              </p>
            </div>

            <!-- Section 7 -->
            <div id="section-7" class="legal-section">
              <div class="section-number-tag">07</div>
              <h2 class="section-title">Intellectual Property Rights</h2>
              <p>
                All software source code, proprietary algorithms, database schemas, graphic designs, interfaces, and trademarks associated with JewelBiz remain the exclusive intellectual property of JewelBiz.
              </p>
              <p>
                Subscribers may not reverse engineer, decompile, copy, or redistribute any part of the JewelBiz platform or its source code.
              </p>
            </div>

            <!-- Section 8 -->
            <div id="section-8" class="legal-section">
              <div class="section-number-tag">08</div>
              <h2 class="section-title">Limitation of Liability & Jurisdiction</h2>
              <p>
                In no event shall JewelBiz be liable for indirect, incidental, or consequential damages resulting from local network outages, weighing scale hardware miscalibrations, or erroneous operator manual data entry.
              </p>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising under this agreement shall be subject to the exclusive jurisdiction of the competent courts in <strong>Kochi, Kerala, India</strong>.
              </p>
            </div>

            <div class="doc-footer-actions">
              <router-link to="/privacy-policy" class="btn-doc-link">
                ← Read Privacy Policy
              </router-link>
              <router-link to="/book-demo" class="btn-doc-primary">
                Book a Free Software Demo
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="legal-page-footer">
      <div class="container-luxury footer-flex">
        <p class="footer-copy">
          &copy; {{ new Date().getFullYear() }} JewelBiz. All rights reserved.
        </p>
        <div class="footer-nav-links">
          <router-link to="/">Home</router-link>
          <span class="dot">•</span>
          <router-link to="/privacy-policy">Privacy Policy</router-link>
          <span class="dot">•</span>
          <router-link to="/book-demo">Book Demo</router-link>
          <span class="dot">•</span>
          <router-link to="/login">Login</router-link>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: "TermsConditions",
  data() {
    return {
      isScrolled: false,
      activeSection: "section-1"
    };
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll);
    this.handleScroll();
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 40;
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@500;600;700;800&family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,600&family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400;1,500&display=swap');

.legal-page {
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  background-color: #FAF8F5;
  color: #1E1815;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.container-luxury {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  width: 100%;
}

/* Header */
.main-header {
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background: rgba(250, 248, 245, 0.94);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(158, 109, 43, 0.12);
  transition: all 0.3s ease;
}

.main-header.scrolled {
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 4px 20px rgba(158, 109, 43, 0.08);
}

.header-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 14px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.brand-logo-link {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
}

.logo-mark {
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.diamond-svg {
  width: 100%;
  height: 100%;
}

.logo-text-block {
  display: flex;
  flex-direction: column;
}

.brand-name {
  font-family: 'Cinzel', serif;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 2.2px;
  color: #1E1815;
  line-height: 1.1;
}

.brand-highlight {
  color: #9E6D2B;
}

.brand-subtext {
  font-size: 8.5px;
  font-weight: 600;
  letter-spacing: 1.2px;
  color: #7D551E;
}

.desktop-nav {
  display: flex;
  align-items: center;
  gap: 24px;
}

.nav-link {
  font-size: 14px;
  font-weight: 600;
  color: #554438;
  text-decoration: none;
  transition: color 0.2s ease;
  display: inline-flex;
  align-items: center;
}

.nav-link:hover {
  color: #9E6D2B;
}

.btn-signin-gold {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  background: linear-gradient(135deg, #B27E36 0%, #8E5E20 100%);
  color: #FFFFFF !important;
  font-size: 13px;
  font-weight: 600;
  border-radius: 30px;
  text-decoration: none;
  box-shadow: 0 4px 14px rgba(158, 109, 43, 0.25);
  transition: all 0.25s ease;
}

.btn-signin-gold:hover {
  background: linear-gradient(135deg, #C28F43 0%, #9B6925 100%);
  transform: translateY(-1px);
}

.btn-back-nav {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background: rgba(158, 109, 43, 0.08);
  border: 1px solid rgba(158, 109, 43, 0.25);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  color: #7D551E;
  text-decoration: none;
}

/* Hero */
.legal-hero {
  padding: 60px 0 40px;
  background: linear-gradient(180deg, #F5EFE6 0%, #FAF8F5 100%);
  border-bottom: 1px solid #EFE8DD;
  text-align: center;
}

.legal-kicker {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 2.5px;
  color: #9E6D2B;
  margin-bottom: 12px;
}

.legal-title {
  font-family: 'Cinzel', serif;
  font-size: clamp(32px, 4vw, 44px);
  font-weight: 700;
  color: #1E1815;
  margin-bottom: 12px;
  line-height: 1.2;
}

.legal-subtitle {
  font-size: clamp(15px, 2vw, 17px);
  color: #6B5E55;
  max-width: 680px;
  margin: 0 auto 24px;
  line-height: 1.6;
}

.meta-badge-row {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
}

.meta-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  background: #FFFFFF;
  border: 1px solid rgba(158, 109, 43, 0.25);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  color: #554438;
  box-shadow: 0 2px 6px rgba(158, 109, 43, 0.05);
}

/* Layout Grid */
.legal-content-section {
  padding: 50px 0 80px;
  flex: 1;
}

.legal-layout-grid {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 40px;
  align-items: flex-start;
}

@media (max-width: 960px) {
  .legal-layout-grid {
    grid-template-columns: 1fr;
  }
}

/* Sidebar */
.sidebar-sticky-card {
  position: sticky;
  top: 90px;
  background: #FFFFFF;
  border: 1px solid #EFE8DD;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 6px 20px rgba(30, 24, 21, 0.04);
}

.sidebar-heading {
  font-family: 'Cinzel', serif;
  font-size: 15px;
  font-weight: 700;
  color: #1E1815;
  margin-bottom: 16px;
  letter-spacing: 0.5px;
}

.toc-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.toc-list a {
  font-size: 13.5px;
  font-weight: 500;
  color: #554438;
  text-decoration: none;
  display: block;
  padding: 6px 10px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.toc-list a:hover {
  background: rgba(158, 109, 43, 0.08);
  color: #9E6D2B;
}

.sidebar-link-btn {
  display: block;
  text-align: center;
  padding: 10px 14px;
  background: rgba(158, 109, 43, 0.08);
  border: 1px solid rgba(158, 109, 43, 0.3);
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #7D551E;
  text-decoration: none;
  transition: all 0.2s ease;
}

.sidebar-link-btn:hover {
  background: #9E6D2B;
  color: #FFFFFF;
}

/* Body Card */
.legal-body-card {
  background: #FFFFFF;
  border: 1px solid #EFE8DD;
  border-radius: 20px;
  padding: clamp(28px, 4vw, 48px);
  box-shadow: 0 8px 30px rgba(30, 24, 21, 0.04);
}

.legal-section {
  position: relative;
  padding-bottom: 36px;
  margin-bottom: 36px;
  border-bottom: 1px solid #F0EAE1;
}

.legal-section:last-of-type {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-number-tag {
  display: inline-block;
  font-family: 'Cinzel', serif;
  font-size: 12px;
  font-weight: 700;
  color: #9E6D2B;
  letter-spacing: 1px;
  margin-bottom: 4px;
}

.section-title {
  font-family: 'Cinzel', serif;
  font-size: clamp(20px, 2.5vw, 24px);
  font-weight: 700;
  color: #1E1815;
  margin-bottom: 16px;
  line-height: 1.3;
}

.legal-section p {
  font-size: 15px;
  line-height: 1.75;
  color: #4A3E36;
  margin-bottom: 14px;
}

.styled-legal-list {
  padding-left: 20px;
  margin: 14px 0 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.styled-legal-list li {
  font-size: 14.5px;
  line-height: 1.7;
  color: #4A3E36;
}

.styled-legal-list strong {
  color: #1E1815;
}

.callout-gold-box {
  background: linear-gradient(135deg, rgba(245, 235, 220, 0.7) 0%, rgba(255, 248, 235, 0.9) 100%);
  border-left: 4px solid #9E6D2B;
  border-radius: 8px;
  padding: 14px 18px;
  margin: 16px 0;
  font-size: 14.5px;
  color: #5A3F1A;
  display: flex;
  align-items: center;
}

.doc-footer-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 40px;
  padding-top: 24px;
  border-top: 1px solid #EFE8DD;
}

.btn-doc-link {
  font-size: 14px;
  font-weight: 600;
  color: #9E6D2B;
  text-decoration: none;
}

.btn-doc-primary {
  padding: 10px 22px;
  background: linear-gradient(135deg, #B27E36 0%, #8E5E20 100%);
  color: #FFFFFF !important;
  border-radius: 25px;
  font-size: 13.5px;
  font-weight: 600;
  text-decoration: none;
  box-shadow: 0 4px 14px rgba(158, 109, 43, 0.22);
}

/* Footer */
.legal-page-footer {
  background: #FAF7F2;
  border-top: 1px solid #EFE8DD;
  padding: 24px 0;
}

.footer-flex {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.footer-copy {
  font-size: 13px;
  color: #7D6F64;
  margin-bottom: 0;
}

.footer-nav-links {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
}

.footer-nav-links a {
  color: #554438;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.footer-nav-links a:hover {
  color: #9E6D2B;
}

.dot {
  color: #C5BAAD;
}
</style>
